/**
 * Detects the Twilio Voice SDK's unhandled `NotReadableError` from re-opening
 * the microphone after an audio device change.
 *
 * When the browser fires `devicechange` (a headset connects or switches
 * profile, the OS default mic changes), the SDK's `AudioHelper` stops the
 * call's mic tracks and calls `getUserMedia` again from a `setTimeout`, with
 * no `.catch`. If the OS cannot start the mic (another app holds it, or the
 * device is still switching), the rejection escapes as an unhandled
 * `NotReadableError` with no stack frames, so none of our code can catch it.
 *
 * `useVoiceCallSession` tells the admin about it in-app, so the RUM and log
 * copies are dropped rather than paging as an uncaught error. First-party
 * `getUserMedia` calls all catch their own rejections, so a frameless one is
 * the SDK's.
 */
export function isTwilioMicReacquireFailure(
  message: string | undefined,
  stack: string | undefined,
  kind: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (kind !== "NotReadableError" && kind !== "TrackStartError") {
    return false;
  }

  if (!_AUDIO_SOURCE_MESSAGE_PATTERN.test(message)) {
    return false;
  }

  if (!stack) {
    return true;
  }

  // Datadog serializes a frameless DOMException's stack as just the message.
  // V8 frames start with "at "; Firefox frames use function@URL:line:column.
  return !stack.split("\n").some((line) => {
    const trimmedLine = line.trim();
    return trimmedLine.startsWith("at ") || /@.+:\d+:\d+$/.test(trimmedLine);
  });
}

// Chrome: "Could not start audio source". Firefox: "Failed to allocate audiosource".
const _AUDIO_SOURCE_MESSAGE_PATTERN =
  /could not start audio source|failed to allocate audiosource/i;
